/**
 * Canonical crop helpers for recovery relock (matches js_track_with_relock.mjs).
 * Worker-safe: uses OffscreenCanvas when document/HTMLCanvasElement are absent.
 */
import { mat3Mul } from "./homography.js";

function makeCanvas(w, h) {
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(w, h);
  if (typeof document !== "undefined") {
    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    return c;
  }
  throw new Error("No canvas implementation available");
}

function isCanvasLike(x) {
  return !!x && typeof x.getContext === "function" && Number.isFinite(x.width) && Number.isFinite(x.height);
}

export function projectH(H, x, y) {
  const w = H[6] * x + H[7] * y + H[8];
  return [(H[0] * x + H[1] * y + H[2]) / w, (H[3] * x + H[4] * y + H[5]) / w];
}

export function quadFromH(H, renderSize = 1200) {
  return [[0, 0], [renderSize, 0], [renderSize, renderSize], [0, renderSize]]
    .map(([x, y]) => projectH(H, x, y));
}

export function rgbaFromCanvas(canvas) {
  const w = canvas.width;
  const h = canvas.height;
  const { data } = canvas.getContext("2d", { willReadFrequently: true }).getImageData(0, 0, w, h);
  return { data, w, h };
}

export function makeCanonicalRgba(frameRgba, H, opts = {}) {
  const targetBoardPx = opts.targetBoardPx ?? 500;
  const margin = opts.margin ?? 0.4;
  const canvas = opts.canvasPx ?? Math.round(targetBoardPx * 1.25);
  const quad = quadFromH(H, opts.renderSize ?? 1200);
  const xs = quad.map((p) => p[0]);
  const ys = quad.map((p) => p[1]);
  let x0 = Math.min(...xs);
  let y0 = Math.min(...ys);
  let x1 = Math.max(...xs);
  let y1 = Math.max(...ys);
  const bw = x1 - x0;
  const bh = y1 - y0;
  x0 = Math.max(0, Math.floor(x0 - bw * margin));
  y0 = Math.max(0, Math.floor(y0 - bh * margin));
  x1 = Math.min(frameRgba.w, Math.ceil(x1 + bw * margin));
  y1 = Math.min(frameRgba.h, Math.ceil(y1 + bh * margin));
  const cropW = Math.max(1, x1 - x0);
  const cropH = Math.max(1, y1 - y0);
  const scale = targetBoardPx / Math.max(cropW, cropH);
  const contentW = Math.max(1, Math.round(cropW * scale));
  const contentH = Math.max(1, Math.round(cropH * scale));
  const padX = Math.max(0, Math.floor((canvas - contentW) / 2));
  const padY = Math.max(0, Math.floor((canvas - contentH) / 2));
  const out = new Uint8ClampedArray(canvas * canvas * 4);
  const src = frameRgba.data;
  const sw = frameRgba.w;
  const sh = frameRgba.h;
  for (let y = 0; y < contentH; y++) {
    const fy = y0 + ((y + 0.5) * cropH / contentH - 0.5);
    const sy = Math.max(0, Math.min(sh - 1, fy));
    const yi = Math.floor(sy);
    const yj = Math.min(yi + 1, sh - 1);
    const ty = sy - yi;
    for (let x = 0; x < contentW; x++) {
      const fx0 = x0 + ((x + 0.5) * cropW / contentW - 0.5);
      const sx = Math.max(0, Math.min(sw - 1, fx0));
      const xi = Math.floor(sx);
      const xj = Math.min(xi + 1, sw - 1);
      const tx = sx - xi;
      const i00 = (yi * sw + xi) * 4;
      const i10 = (yi * sw + xj) * 4;
      const i01 = (yj * sw + xi) * 4;
      const i11 = (yj * sw + xj) * 4;
      const oi = ((padY + y) * canvas + (padX + x)) * 4;
      for (let c = 0; c < 4; c++) {
        out[oi + c] = Math.round(
          src[i00 + c] * (1 - tx) * (1 - ty)
          + src[i10 + c] * tx * (1 - ty)
          + src[i01 + c] * (1 - tx) * ty
          + src[i11 + c] * tx * ty,
        );
      }
    }
  }
  const H_canonical_to_frame = [1 / scale, 0, x0 - padX / scale, 0, 1 / scale, y0 - padY / scale, 0, 0, 1];
  return { data: out, w: canvas, h: canvas, H_canonical_to_frame };
}

export function transformRelockToFrame(lock, Hcf) {
  const H_raw = mat3Mul(Hcf, lock.H_raw);
  const H_display = mat3Mul(Hcf, lock.H_display || lock.H_raw);
  const frame_pts = lock.frame_pts.map(([x, y]) => projectH(Hcf, x, y));
  return { ...lock, H_raw, H_display, frame_pts, kind: "canon-local" };
}

export function canonicalCanvasFromFrame(frameSource, H, opts = {}) {
  const srcCanvas = isCanvasLike(frameSource)
    ? frameSource
    : (() => {
      const w = frameSource.videoWidth ?? frameSource.naturalWidth ?? frameSource.width;
      const h = frameSource.videoHeight ?? frameSource.naturalHeight ?? frameSource.height;
      if (!w || !h) throw new Error("canonical source has no dimensions");
      const c = makeCanvas(w, h);
      c.getContext("2d", { willReadFrequently: true }).drawImage(frameSource, 0, 0, w, h);
      return c;
    })();
  const canon = makeCanonicalRgba(rgbaFromCanvas(srcCanvas), H, opts);
  const out = makeCanvas(canon.w, canon.h);
  out.getContext("2d", { willReadFrequently: true })
    .putImageData(new ImageData(canon.data, canon.w, canon.h), 0, 0);
  return { canvas: out, H_canonical_to_frame: canon.H_canonical_to_frame };
}
